import { postToBackend } from "./portalClient";
import { colors } from 'theme/tokens';

/**
 * Branding & Theming (Admin portal) — spec §17.1–17.10.
 *
 *   POST /app/v1/branding/view                   the theme
 *   POST /app/v1/branding/update                 "Save Changes"
 *   POST /app/v1/partners/branding/assets/create          the upload zones (MULTIPART)
 *   POST /app/v1/branding/email-templates/list            email list (per vertical)
 *   POST /app/v1/branding/email-templates/save          email save (create OR update)
 *   POST /app/v1/branding/email-templates/delete          email delete (SOFT)
 *   POST /app/v1/branding/document-templates/list         document list (per vertical)
 *   POST /app/v1/branding/document-templates/save       document save (create OR update)
 *   POST /app/v1/branding/document-templates/delete       document delete (SOFT)
 *   POST /app/v1/branding/options                        fonts, verticals, variables, sections
 *
 * Everything except the options lookup is scoped to one partner. Three areas: the
 * brand theme, email templates and document templates.
 *
 * ── ⚠️ The four asset URLs must be HOSTED urls, never object URLs ───────────
 * `logoUrl` / `logoDarkUrl` / `iconUrl` / `faviconUrl` hold URLs that came from
 * §17.3 (or back from §17.1). They are persisted by §17.2 and then rendered in
 * every branded email and document the platform generates.
 *
 * A `URL.createObjectURL(file)` value is scoped to the browser tab that made it:
 * it dies on reload and means nothing to anyone else. Writing one into these
 * fields and saving does not "not work" — it silently stores a dead reference as
 * the partner's logo. Upload first, store the returned `url`, then save.
 *
 * ── ⚠️ Document section toggles are NESTED on the wire ──────────────────────
 * §17.8 takes the three display flags at the top level and every section toggle
 * under `sections`:
 *
 *   { headerTitle, …, showLogo, showPartnerName, showBorder,
 *     sections: { showSectorDetails: true, … } }
 *
 * WHICH keys belong under `sections` depends on the document name and comes from
 * §17.10's `documentSections[name]` — so the options lookup is a dependency of the
 * document save, not just a source of labels. A document name the options do not
 * describe has no known section keys and must send `sections: {}`.
 *
 * ── Templates are keyed by (vertical, name) — no id, no rename ──────────────
 * §17.5/§17.8 CREATE when the name is new and UPDATE when it exists, so one
 * endpoint backs both "Add Template" and "Edit". The caller signals which path
 * it means by passing `created: true` on Add and `created: false` on Edit —
 * the server branches on this rather than inferring from row existence. There
 * is no rename endpoint: a new name creates a second template and leaves the
 * original in place.
 *
 * ⚠️ The name travels RAW. The spec shows it URL-encoded because it sits in the
 * engine's path, but the portal hop takes it in the body — encoding it here would
 * store a literal `Booking%20Confirmation` as the template's name.
 *
 * ── `configured` is server-set ──────────────────────────────────────────────
 * Saving marks a template `configured: true`. It is not sent; it is read back off
 * the response.
 *
 * ── Soft delete ─────────────────────────────────────────────────────────────
 * §17.6/§17.9 stamp `deletedAt` / `deletedBy` and exclude the row from lists.
 * Idempotent; writes to a deleted row answer 409; lists omit it unless
 * `includeDeleted: true`; rows stay restorable by an operator for the retention
 * window. (Contrast §16.6's override reset, which is NOT a soft delete.)
 */

/**
 * The active brand's palette (theme/brands) — shared fallback when no theme is
 * saved. Zenvoya's house palette unless a brand folder is laid over it.
 */
const DEFAULT_BRAND_COLORS = { ...colors.partnerDefaultBranding };

const EMPTY_THEME = {
  ...DEFAULT_BRAND_COLORS,
  fontFamily: "",
  logoUrl: "",
  logoDarkUrl: "",
  iconUrl: "",
  faviconUrl: "",
};

/**
 * Normalise a theme so a partner with no branding row yields the house palette
 * for colours (never a raw ""). A real server hex still wins because `||` only
 * falls through on empty / missing values.
 */
const toTheme = (d) => ({
  primaryColor: d?.primaryColor || DEFAULT_BRAND_COLORS.primaryColor,
  secondaryColor: d?.secondaryColor || DEFAULT_BRAND_COLORS.secondaryColor,
  accentColor: d?.accentColor || DEFAULT_BRAND_COLORS.accentColor,
  fontFamily: d?.fontFamily || "",
  logoUrl: d?.logoUrl || "",
  logoDarkUrl: d?.logoDarkUrl || "",
  iconUrl: d?.iconUrl || "",
  faviconUrl: d?.faviconUrl || "",
  // App Labels — `{ navShop: "Book" }`, only the renamed ones. Carried ONLY when
  // the server sent it: a backend without the field refuses it as unknown, so
  // echoing a defaulted `{}` back would fail every save there.
  ...(d && "uiLabels" in d ? { uiLabels: toUiLabels(d.uiLabels) } : {}),
  // Theme Studio — the stored overrides, or null when the partner has no theme
  // (`resolvedTheme` null = the app's shipped look). Carried only when the server
  // sent it, for the same reason as `uiLabels`.
  ...(d && "theme" in d ? { theme: toThemeOverrides(d) } : {}),
});

/**
 * `view` answers `theme: {}` both for "no theme" and for "Zenvoya preset, nothing
 * changed"; `resolvedTheme` is what tells them apart. A save echoes this back:
 * null clears the theme, an object replaces the overrides.
 */
const toThemeOverrides = (d) => {
  if ("resolvedTheme" in d) return d.resolvedTheme ? asObject(d.theme) : null;
  // The page's own state on the way out (no `resolvedTheme` there).
  return d.theme === null ? null : asObject(d.theme);
};

/** A plain object, or `{}` for anything else. */
const asObject = (raw) =>
  raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};

/** `uiLabels` as an object of label key -> text; anything else reads as none. */
const toUiLabels = asObject;

/**
 * The partner's brand theme (§17.1) — three colours, the font, and the four asset
 * URLs. Every field is defaulted; the page reads them straight into controlled
 * inputs and colour pickers.
 */
export const getBranding = async (partnerId) => {
  const result = await postToBackend("/app/v1/partners/branding/view", { partnerCode: partnerId });
  if (!result.ok) return result;
  return { ok: true, data: toTheme(result.data) };
};

/**
 * Persist the whole theme (§17.2) — "Save Changes". All eight fields are required;
 * the URL fields carry "" when unset.
 *
 * ⚠️ Only hosted URLs belong in the four URL fields. See the header.
 */
export const updateBranding = async (partnerId, theme = {}) => {
  const body = { partnerCode: partnerId, ...toTheme(theme) };
  const result = await postToBackend("/app/v1/partners/branding/update", body);
  if (!result.ok) return result;
  return { ok: true, data: toTheme(result.data) };
};

/**
 * Upload one brand asset (§17.3) and get back its hosted URL.
 *
 * The endpoint takes JSON — the file rides as a base64 data URL under the wire
 * key that matches the slot (`logoFile` / `logoDarkFile` / `iconFile` /
 * `faviconFile`). The caller names the slot by its URL field (`logoUrl` /
 * `logoDarkUrl` / `iconUrl` / `faviconUrl`); the mapping table below translates.
 *
 * The file is validated client-side before the network hop, so a bad choice
 * gives an immediate reason instead of a generic backend rejection. Formats:
 * PNG, JPEG, WebP, SVG, ICO. Max 4 MB.
 *
 * Returns `{ field, url }` on success — `field` is the ORIGINAL URL slot the
 * caller passed (not the wire key), so the caller can store `url` straight into
 * `theme[field]` and then save via §17.2. Validation failures return the
 * standard `{ ok: false, message }` envelope so the existing error path renders
 * them.
 */
const UPLOAD_WIRE_FIELDS = {
  logoUrl: "logoFile",
  logoDarkUrl: "logoDarkFile",
  iconUrl: "iconFile",
  faviconUrl: "faviconFile",
};

const KB = 1024;
const MB = 1024 * KB;

/**
 * Per-slot limits. Only favicon accepts ICO — the rest are SVG/PNG/WebP.
 * `dimensions` only applies to raster formats a browser can decode; SVG (vector)
 * and ICO (multi-entry, browser-dependent natural size) skip the pixel checks.
 * `mustBeSquare` blocks anything where width ≠ height.
 */
const IMG = {
  svg: "image/svg+xml",
  png: "image/png",
  webp: "image/webp",
  ico: ["image/x-icon", "image/vnd.microsoft.icon"],
};
const RASTER_LOGO_MIMES = [IMG.svg, IMG.png, IMG.webp];
const FAVICON_MIMES = [...RASTER_LOGO_MIMES, ...IMG.ico];
/** Formats that skip dimension checks. */
const SKIP_DIMENSION_MIMES = new Set([IMG.svg, ...IMG.ico]);

/**
 * Per-slot size caps.
 *
 * These were 5 MB each, which is about 760x the 6.7 KB SVG a real partner has
 * actually uploaded, and the cost of that headroom is not the download — it is
 * the UPLOAD. The file rides to app-backend as base64 inside an AES-encrypted
 * body, and `CryptoJS.AES.encrypt` runs synchronously on the main thread, so a
 * 5 MB file becomes a 6.8 MB string to encrypt and the operator's tab stops
 * answering for seconds. Whatever survives that is then served to every viewer
 * on every page the partner's logo appears on, which for the sidebar wordmark
 * is all of them.
 *
 * The numbers below are still an order of magnitude above what the artwork
 * needs — a 600x160 PNG is 20-40 KB, a 1200x320 one with transparency about
 * 150 KB, a 512x512 icon around 50 KB — so no real logo meets them. They exist
 * to stop a camera photo or an unexported design file being handed to the
 * platform as a logo.
 */
const LOGO_SLOT_RULES = {
  logoUrl: { label: "Primary Logo", accept: RASTER_LOGO_MIMES, maxBytes: 512 * KB },
  logoDarkUrl: { label: "Dark Variant", accept: RASTER_LOGO_MIMES, maxBytes: 512 * KB },
  iconUrl: {
    label: "Icon", accept: RASTER_LOGO_MIMES, maxBytes: 256 * KB,
    dimensions: { min: 128, max: 512, mustBeSquare: true },
  },
  faviconUrl: {
    label: "Favicon", accept: FAVICON_MIMES, maxBytes: 128 * KB,
    dimensions: { min: 32, max: 128, mustBeSquare: true },
  },
};

/**
 * "512 KB", not "1 MB" — `Math.round(512 KB / MB)` is 1, so the old message
 * told the operator a limit twice the real one and then refused a file that
 * met it.
 */
export const formatBytes = (bytes) => (
  bytes >= MB ? `${+(bytes / MB).toFixed(1)} MB` : `${Math.round(bytes / KB)} KB`
);

/**
 * Union of every slot's allowed MIME — the outer page uses this for a coarse
 * pre-check before delegating to per-slot rules below.
 */
const LOGO_ACCEPTED_MIME = Array.from(
  new Set(Object.values(LOGO_SLOT_RULES).flatMap((r) => r.accept)),
);

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("Could not read file."));
    reader.readAsDataURL(file);
  });

/**
 * Read a raster image's natural pixel size. SVG doesn't go through here — the
 * caller checks the MIME first and skips vector images.
 */
const readImageSize = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const size = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(size);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image dimensions."));
    };
    img.src = url;
  });

/**
 * Format + size + dimension checks for a logo slot — no network.
 *
 * Split out of `uploadBrandAsset` so a file can be validated the moment an
 * operator picks it, before anything is sent anywhere. Uploads are staged
 * now (SMB — "apply only on Save, not on pick"): the file sits as a local
 * preview until Save is pressed, and the actual `postToBackend` call happens
 * then, for every slot with a pending file, right before the branding row is
 * written. Validating early still matters — an operator should not discover
 * a format or size problem only after clicking Save on an unrelated field.
 */
export const validateLogoFile = async (field, file) => {
  const wireField = UPLOAD_WIRE_FIELDS[field];
  if (!wireField) {
    return { ok: false, message: "Unknown upload slot." };
  }
  if (!file) {
    return { ok: false, message: "Please choose a file to upload." };
  }
  const rules = LOGO_SLOT_RULES[field] || {};
  const acceptList = rules.accept || RASTER_LOGO_MIMES;
  if (!acceptList.includes(file.type)) {
    const message = field === "faviconUrl"
      ? "Only SVG, PNG, WebP or ICO files are allowed."
      : "Only SVG, PNG or WebP files are allowed.";
    return { ok: false, message };
  }

  const maxBytes = rules.maxBytes ?? 5 * MB;
  if (file.size > maxBytes) {
    return {
      ok: false,
      message: `File is too large — maximum ${formatBytes(maxBytes)}. This file is ${formatBytes(file.size)}.`,
    };
  }

  // Dimension checks only apply to browser-decodable rasters. SVG (vector) and
  // ICO (multi-entry, engine-dependent natural size) skip this pass.
  if (rules.dimensions && !SKIP_DIMENSION_MIMES.has(file.type)) {
    const { min, max, mustBeSquare } = rules.dimensions;
    let size;
    try {
      size = await readImageSize(file);
    } catch {
      return { ok: false, message: "Could not read image dimensions." };
    }
    const label = rules.label || "Image";
    if (mustBeSquare && size.width !== size.height) {
      return { ok: false, message: `${label} must be a square image. This file is ${size.width}×${size.height}.` };
    }
    if (size.width < min || size.height < min) {
      return { ok: false, message: `${label} must be at least ${min}×${min} px. This file is ${size.width}×${size.height}.` };
    }
    if (size.width > max || size.height > max) {
      return { ok: false, message: `${label} can be at most ${max}×${max} px. This file is ${size.width}×${size.height}.` };
    }
  }

  return { ok: true };
};

/**
 * The real upload — called only at Save time now, once per slot that has a
 * pending file. Re-runs `validateLogoFile` rather than trusting the pick-time
 * check: the file object a component held onto is still the same File, so
 * this is cheap, but it is also the only guard against a slot's rules having
 * changed between pick and Save in a long-open dialog.
 */
export const uploadBrandAsset = async (partnerId, field, file) => {
  const validation = await validateLogoFile(field, file);
  if (!validation.ok) return validation;

  const wireField = UPLOAD_WIRE_FIELDS[field];
  let dataUrl;
  try {
    dataUrl = await fileToDataUrl(file);
  } catch {
    return { ok: false, message: "Could not read the selected file." };
  }

  const body = {
    partnerCode: partnerId,
    [wireField]: { contentType: file.type, data: dataUrl },
  };
  const result = await postToBackend("/app/v1/partners/branding/logos/upload", body);
  if (!result.ok) return result;
  // The response echoes the whole branding row — the hosted URL lands at
  // data[field] (e.g. `data.logoUrl`), not at `data.url`. `data.uploaded` lists
  // which slot(s) were touched by this call; we only surface the one URL the
  // caller asked about.
  return {
    ok: true,
    data: { field, url: result.data?.[field] || "" },
  };
};

/* ══════════════════════════════════════════════════════════════════════
   EMAIL TEMPLATES
   ══════════════════════════════════════════════════════════════════════ */

/** Email templates for one partner + vertical (§17.4). Returns a flat array. */
export const listEmailTemplates = async (partnerId, vertical, { includeDeleted } = {}) => {
  const body = { partnerCode: partnerId, productType: vertical };
  if (includeDeleted) body.includeDeleted = true;
  const result = await postToBackend("/app/v1/partners/branding/email-templates/list", body);
  if (!result.ok) return result;
  return { ok: true, data: result.data?.templates || [] };
};

/**
 * Create or update an email template (§17.5).
 *
 * `template` carries only the six editable fields — subject, greeting, body,
 * ctaText, ctaUrl, footerText. `name` and `vertical` are the key, and `configured`
 * is server-set.
 */
export const saveEmailTemplate = (partnerId, vertical, name, template = {}, created = false) =>
  postToBackend("/app/v1/partners/branding/email-templates/update", {
    partnerCode: partnerId,
    productType: vertical,
    templateCode:
      template.templateCode ||
      String(name || "").trim().toUpperCase().replace(/\s+/g, "_"),
    // created: !!created,
    name,
    subject: template.subject || "",
    greeting: template.greeting || "",
    bodyHtml: template.body || "",
    ctaButtonText: template.ctaText || "",
    ctaButtonUrl: template.ctaUrl || "",
    footerNote: template.footerText || "",
    availableVariables: Array.isArray(template.availableVariables)
      ? template.availableVariables
      : [],
  });

/** Soft-delete an email template (§17.6). Returns the soft-delete envelope. */
export const deleteEmailTemplate = (partnerId, vertical, name, templateCode) =>
  postToBackend("/app/v1/partners/branding/email-templates/delete", {
    partnerCode: partnerId,
    productType: vertical,
    templateCode:
      templateCode ||
      String(name || "").trim().toUpperCase().replace(/\s+/g, "_"),
  });

/* ══════════════════════════════════════════════════════════════════════
   DOCUMENT TEMPLATES
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Document templates for one partner + vertical (§17.7). Returns a flat array;
 * each row carries its section toggles NESTED under `sections`.
 */
export const listDocTemplates = async (partnerId, vertical, { includeDeleted } = {}) => {
  const body = { partnerCode: partnerId, productType: vertical };
  if (includeDeleted) body.includeDeleted = true;
  const result = await postToBackend("/app/v1/partners/branding/document-templates/list", body);
  if (!result.ok) return result;
  return { ok: true, data: result.data?.templates || [] };
};

/**
 * Create or update a document template (§17.8).
 *
 * ⚠️ `sections` must already be the nested object — the caller splits the flat
 * form state using §17.10's section keys for this document name. See the header.
 */
export const saveDocTemplate = (partnerId, vertical, name, template = {}, created = false) =>
  postToBackend("/app/v1/partners/branding/document-templates/update", {
    partnerCode: partnerId,
    templateCode:
      template.templateCode ||
      String(name || "").trim().toUpperCase().replace(/\s+/g, "_"),
    productType: vertical,
    // created: !!created,
    name,
    documentTitle: template.headerTitle || "",
    subtitle: template.headerSubtitle || "",
    displayOptions: {
      showLogo: !!template.showLogo,
      showPartnerName: !!template.showPartnerName,
      showBorder: !!template.showBorder,
    },
    sections: template.sections || {},
    footerHtml: template.footerText || "",
    footerNote: template.footerNote || "",
    publishState: template.publishState || "published",
  });

/** Soft-delete a document template (§17.9). Returns the soft-delete envelope. */
export const deleteDocTemplate = (partnerId, vertical, name, templateCode) =>
  postToBackend("/app/v1/partners/branding/document-templates/delete", {
    partnerCode: partnerId,
    productType: vertical,
    templateCode:
      templateCode ||
      String(name || "").trim().toUpperCase().replace(/\s+/g, "_"),
  });

/* ══════════════════════════════════════════════════════════════════════
   REFERENCE
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Reference data for the pickers (§17.10). Takes no parameters.
 *
 * Returns `fonts`, `verticals`, `templateVariables` and `documentSections` — the
 * last being a map of document name → [{ key, label }], which the document save
 * needs to know which flags are section toggles.
 *
 * Note it returns variable NAMES, not sample renderings, so a preview still needs
 * its own sample values.
 */
export const getBrandingOptions = async () => {
  const result = await postToBackend("/app/v1/branding/options", {});
  if (!result.ok) return result;
  const d = result.data || {};
  return {
    ok: true,
    data: {
      fonts: d.fonts || [],
      // app-backend 75fca8b (22 Aug) sends this list as `productTypes`;
      // `verticals` is the older name, kept as a fallback.
      verticals: d.productTypes || d.verticals || [],
      templateVariables: d.templateVariables || [],
      documentSections: d.documentSections || {},
      // `[{ key, label, default, maxLength }]` — the App Labels the partner may
      // rename. Empty on a backend without the feature, which hides the card.
      uiLabels: Array.isArray(d.uiLabels) ? d.uiLabels : [],
      // Theme Studio catalogue — presets, scales, colours, components. Null on a
      // backend without the feature, which hides the studio.
      theme: d.theme && Array.isArray(d.theme.presets) ? d.theme : null,
    },
  };
};

export { EMPTY_THEME, DEFAULT_BRAND_COLORS, LOGO_ACCEPTED_MIME, LOGO_SLOT_RULES };
